import dayjs from 'dayjs';
import { COOKIE_STORAGE } from '@helpers';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UserRole } from '@base/types/user';

import { UserState } from './types';
import { RootState } from '../types';

export const initialState: UserState = {
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    role: [],
    customerTypeId: '',
    token: '',
    timestamp: dayjs().toISOString(),
    createdDate: '',
    rememberMe: false,
    phoneNumber: '',
};

const loadUserDataFromLocalStorage = () => {
    try {
        const serializedData = COOKIE_STORAGE.getItem('applicationAuth') as UserState | null;
        if (serializedData === null) {
            return initialState;
        }

        const now = dayjs();

        const hours = serializedData.rememberMe ? 168 : 48;
        const diffHours = now.diff(dayjs(serializedData.timestamp), 'hours');
        const diffSeconds = dayjs(serializedData.timestamp).add(hours, 'hours').diff(now, 'seconds');

        if (diffHours > hours) return initialState;

        setTimeout(() => {
            COOKIE_STORAGE.removeItem('applicationAuth');
            window.location.href = '/login';
        }, diffSeconds * 1000);

        return serializedData;
    } catch (error) {
        console.error('Failed to load user data from localStorage:', error);
        return initialState;
    }
};

const userSlice = createSlice({
    name: 'user',
    initialState: loadUserDataFromLocalStorage,
    reducers: {
        addUser(state: UserState, action: PayloadAction<UserState>) {
            COOKIE_STORAGE.setItem('applicationAuth', action.payload);
            Object.assign(state, action.payload);
        },
        removeUser(state) {
            COOKIE_STORAGE.removeItem('applicationAuth');
            window.location.href = '/login';
            Object.assign(state, initialState);
        },
    },
});

export const { addUser, removeUser } = userSlice.actions;

export default userSlice.reducer;

export const isInRole = (role: UserRole) => createSelector([(state: RootState) => state.user], (user) => user.role.includes(role));
export const userSelector = createSelector([(state: RootState) => state.user], (user) => user);
